import { useLang } from 'hooks/lang';

import { FullProjectIssue } from 'entities';

import { Table, Column, Row, IconButton, Card } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';
import { EntityStatusTag } from 'components/Entities';

import { ChatIcon, ExpandIcon, ListToDoIcon, UserIcon } from 'components/Icons';

import { CreateIssueButton } from './CreateButton';

import { formatDate } from 'utils/date';

interface IssueListProps {
  children?: never;
  issues: FullProjectIssue[];
  registerNewIssue: (newIssue: FullProjectIssue) => void;
  openIssue: (id: string) => void;
}

export function IssueList({ issues, registerNewIssue, openIssue }: IssueListProps) {
  const { t } = useLang();

  return (
    <Column gap="6">
      <Row justifyContent="space-between">
        <h6>{t('project-issues')}</h6>

        <CreateIssueButton registerNewIssue={registerNewIssue} />
      </Row>

      <Card>
        <Table
          items={issues}
          uniqueKey="id"
          spacing="medium"
          columns={[
            {
              label: t('date') as string,

              render: ({ startDate }) => formatDate(startDate),

              className: 'w-28',
            },
            {
              label: t('type') as string,
              render: ({ type }) => t(type),
            },

            {
              label: t('sub-type') as string,
              render: ({ subType, other }) => `${t(subType)}${other ? `(${other})` : ''}`,
            },

            {
              label: t('created-by') as string,
              render: ({ owner }) => <UserAvatar size={32} user={owner} name />,
            },

            {
              label: t('status') as string,
              className: 'text-center',

              render: ({ status }) => (
                <EntityStatusTag target="issues" status={status} size="medium" />
              ),
            },

            {
              className: 'w-60',

              render: (issue) => {
                return (
                  <Row justifyContent="flex-end">
                    <Row gap="1">
                      <span className="font-sm">
                        {issue.salesInvolved.length + issue.executorsInvolved.length + 1}
                      </span>

                      <UserIcon />
                    </Row>

                    <Row gap="1">
                      <span className="font-sm">{issue.comments.length}</span>

                      <ChatIcon />
                    </Row>

                    <Row gap="1">
                      <span className="font-sm">{issue.actions.length}</span>

                      <ListToDoIcon />
                    </Row>

                    <IconButton size="small" onClick={() => openIssue(issue.id)}>
                      <ExpandIcon />
                    </IconButton>
                  </Row>
                );
              },
            },
          ]}
        />
      </Card>
    </Column>
  );
}
